import { Input } from "@/components/ui/input";
import { Calendar, AlertTriangle } from "lucide-react";

/**
 * Mandatory date-picker shown at the top of /catalog (Iter 31/34).
 * Iter 34: removed the left "icon column" — the icon now lives inline with
 * the "Passo 1" label so inputs span the full card width on mobile (no
 * accidental indentation, perfectly aligned with the locked placeholder
 * below).
 *
 * Props: see Catalog.jsx callsite.
 */
export default function CatalogDateGate({
  startDate, endDate, onStartChange, onEndChange,
  minPickup, dateError, dateWarning, datesValid, isEN,
}) {
  return (
    <div
      className="rounded-2xl border-2 border-[#FFD700]/40 bg-gradient-to-br from-[#FFD700]/[0.08] via-zinc-900/40 to-transparent p-5"
      data-testid="catalog-date-gate"
    >
      <div className="flex items-center gap-2 mb-1">
        <Calendar className="w-4 h-4 text-[#FFD700] shrink-0" />
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#FFD700]">
          {isEN ? "Step 1 — your dates" : "Passo 1 — le tue date"}
        </p>
      </div>
      <p className="font-display text-base sm:text-lg font-bold leading-tight text-zinc-100">
        {isEN
          ? "Pick your rental dates to see what's available."
          : "Scegli le date del noleggio per vedere ciò che è disponibile."}
      </p>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-[11px] uppercase tracking-widest text-zinc-400 block mb-1">
            {isEN ? "Pickup" : "Ritiro"} <span className="text-[#FFD700]">*</span>
          </label>
          <Input
            type="date"
            min={minPickup}
            value={startDate}
            onChange={(e) => onStartChange(e.target.value)}
            className="bg-zinc-950 border-zinc-800 focus:ring-[#FFD700]/50 w-full"
            data-testid="catalog-date-start"
          />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-widest text-zinc-400 block mb-1">
            {isEN ? "Return" : "Riconsegna"} <span className="text-[#FFD700]">*</span>
          </label>
          <Input
            type="date"
            min={startDate || minPickup}
            value={endDate}
            onChange={(e) => onEndChange(e.target.value)}
            className="bg-zinc-950 border-zinc-800 focus:ring-[#FFD700]/50 w-full"
            data-testid="catalog-date-end"
          />
        </div>
      </div>
      {dateError && (
        <div
          className="mt-3 flex items-start gap-2 text-[12.5px] text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2"
          data-testid="catalog-date-error"
        >
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{dateError}</span>
        </div>
      )}
      {dateWarning && (
        <div
          className="mt-3 flex items-start gap-2 text-[12.5px] text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2"
          data-testid="catalog-date-warning"
        >
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-amber-300" />
          <span>{dateWarning}</span>
        </div>
      )}
      {!datesValid && !dateError && (
        <p className="mt-2 text-[12px] text-zinc-500">
          {isEN
            ? "Minimum 48h lead-time online. Need a same-day rental? Call us or message us on WhatsApp."
            : "Online accettiamo prenotazioni con almeno 48h di anticipo. Per ritiro in giornata chiamaci o scrivici su WhatsApp."}
        </p>
      )}
    </div>
  );
}
